/**
 * Tiny seedable PRNG (mulberry32). Deterministic given a seed so the
 * branch generator produces a reproducible layout for tests, while the
 * studio page seeds it from the clock for a fresh tree every load.
 *
 * Not cryptographic — we only need cheap, well-distributed floats.
 */

export interface Rng {
  /** Uniform float in [0, 1). */
  next(): number;
  /** Uniform float in [min, max). */
  range(min: number, max: number): number;
  /** Uniform integer in [min, max] inclusive. */
  int(min: number, max: number): number;
  /** True with probability `p`. */
  chance(p: number): boolean;
}

export function createRng(seed: number): Rng {
  // Mulberry32: 32-bit state, fast, good enough distribution for visuals.
  let state = seed >>> 0;
  const next = (): number => {
    state |= 0;
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  return {
    next,
    range: (min, max) => min + next() * (max - min),
    int: (min, max) => Math.floor(min + next() * (max - min + 1)),
    chance: (p) => next() < p,
  };
}
